import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { unwrapVioSuccessData } from '../utils/vio-api-response';
import { uploadAssetUrl } from '../utils/upload-asset-url';
import {
  enrichHttpErrorForMessage,
  extractHttpErrorMessage,
  pickMessageFromApiBody,
} from '../utils/http-error-message';

@Injectable({
  providedIn: 'root'
})
export class CommonApiService {

  public userInfo: any;

  constructor(private http: HttpClient) { }

  generateHeaderForAPIRequest(): HttpHeaders {
    const token = localStorage.getItem('authToken');

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      authfor: 'W',
      'Content-Type': 'application/json'
    });
  }

  getApiCall(url: string, params: any = null): Observable<any> {
    let headers = this.generateHeaderForAPIRequest();
    let httpParams = new HttpParams();

    if (params) {
      httpParams = httpParams.append('criteria', params);
    }

    return this.http.get<any>(url, { headers, params: httpParams }).pipe(
      map((res) => unwrapVioSuccessData(res)),
      catchError((err) =>
        from(enrichHttpErrorForMessage(err)).pipe(switchMap((e) => throwError(() => e))),
      ),
    );
  }

  postApiCall(url: string, body: any): Observable<any> {
    let headers = this.generateHeaderForAPIRequest();

    // Convention: POST payload should be under body.criteria
    const payload =
      body instanceof FormData
        ? body
        : body && typeof body === 'object' && 'criteria' in body
          ? body
          : { criteria: body ?? {} };

    return this.http.post<any>(url, payload, { headers }).pipe(
      map((res) => unwrapVioSuccessData(res)),
      catchError((err) =>
        from(enrichHttpErrorForMessage(err)).pipe(switchMap((e) => throwError(() => e))),
      ),
    );
  }

  /**
   * Binary download. Parses JSON error bodies (common when backend returns ApplicationSuccess on 4xx/5xx)
   * so callers get a readable message via extractHttpErrorMessage / extractApiErrorMessage.
   */
  postApiCallForFile(url: string, body: any): Observable<Blob> {
    let headers = this.generateHeaderForAPIRequest();

    return this.http
      .post(url, body, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        switchMap((resp: HttpResponse<Blob>) => this.blobResponseOrThrow(resp)),
        catchError((err) =>
          from(enrichHttpErrorForMessage(err)).pipe(switchMap((e) => throwError(() => e))),
        ),
      );
  }

  private blobResponseOrThrow(resp: HttpResponse<Blob>): Observable<Blob> {
    const blob = resp.body;
    if (!blob) {
      return throwError(() => ({
        status: resp.status,
        statusText: resp.statusText,
        message: 'Empty download response. Processing may not have completed.',
        error: { message: 'Empty download response. Processing may not have completed.' },
      }));
    }

    const ct = `${resp.headers.get('Content-Type') || ''} ${blob.type || ''}`.toLowerCase();
    const looksJson = ct.includes('json');

    if (resp.status >= 400 || looksJson) {
      return from(blob.text()).pipe(
        switchMap((text) => this.throwFromJsonDownloadBody(text, resp.status >= 400 ? resp.status : 500)),
      );
    }

    if (blob.size === 0) {
      return throwError(() => ({
        status: 409,
        message: 'Download was empty. Processing may not have completed on the server.',
        error: { message: 'Download was empty. Processing may not have completed on the server.' },
      }));
    }

    return of(blob);
  }

  private throwFromJsonDownloadBody(text: string, status: number): Observable<never> {
    const t = String(text || '').trim();
    let parsed: Record<string, unknown> | null = null;
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        parsed = JSON.parse(t) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
    }
    const msg =
      (parsed && pickMessageFromApiBody(parsed)) ||
      t ||
      extractHttpErrorMessage({ status, error: parsed || { message: t } }) ||
      'Download failed. Processing may not have completed on the server.';
    return throwError(() => ({
      status: status >= 400 ? status : 500,
      statusText: 'Error',
      message: msg,
      error: parsed || { message: msg },
    }));
  }

  deleteApiCall(url: string): Observable<any> {
    let headers = this.generateHeaderForAPIRequest();

    return this.http.delete<any>(url, { headers }).pipe(
      map((res) => unwrapVioSuccessData(res)),
      catchError((err) =>
        from(enrichHttpErrorForMessage(err)).pipe(switchMap((e) => throwError(() => e))),
      ),
    );
  }

  /** GET a file from the upload root (same auth as API). Path is relative to upload root, e.g. `lessons/u1_l2/file.pptx`. */
  downloadUploadRelativeFile(relativePath: string): Observable<Blob> {
    const url = uploadAssetUrl(relativePath);
    const headers = this.generateHeaderForAPIRequest();
    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' }).pipe(
      switchMap((resp: HttpResponse<Blob>) => this.blobResponseOrThrow(resp)),
      catchError((err) =>
        from(enrichHttpErrorForMessage(err)).pipe(switchMap((e) => throwError(() => e))),
      ),
    );
  }

  triggerBrowserFileDownload(blob: Blob, fileName: string): void {
    const name = (fileName || 'download').replace(/[/\\?*:|"<>]+/g, '_');
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

}
